<template>
    <div 
        v-if="pagecount > 1"
        class="pagination-inline"
        data-testid="pagination"
    >
        <div class="pagination-inline__content" data-testid="pagination__row">
            <!-- Left section: Pagination controls -->
            <div class="pagination-inline__controls" data-testid="pagination__controls">
                <button
                    type="button"
                    class="pagination-inline__nav-btn"
                    :disabled="currentPage <= 1"
                    aria-label="Previous page"
                    data-testid="pagination__prev"
                    @click="goToPreviousPage"
                >
                    <img
                        src="/img/navicon/LeftArrow.svg"
                        alt=""
                        class="pagination-inline__nav-icon pagination-inline__nav-icon--prev"
                    />
                </button>
                <v-pagination
                    v-model="currentPage"
                    class="pagination-inline__pagination"
                    :length="pagecount"
                    :total-visible="totalVisible"
                    data-testid="pagination__pagination"
                ></v-pagination>
                <button
                    type="button"
                    class="pagination-inline__nav-btn"
                    :disabled="currentPage >= pagecount"
                    aria-label="Next page"
                    data-testid="pagination__next"
                    @click="goToNextPage"
                >
                    <img
                        src="/img/navicon/LeftArrow.svg"
                        alt=""
                        class="pagination-inline__nav-icon"
                    />
                </button>
            </div>

            <!-- Right section: Page jump -->
            <div class="pagination-inline__right" data-testid="pagination__right">
                <div
                    class="pagination-inline__goto"
                    data-testid="pagination__go-to-container"
                    @keydown.enter.prevent="goToPage"
                    @focusout="onGotoFocusOut"
                >
                    <span class="pagination-inline__goto-label" data-testid="pagination__go-to-text">Page</span>
                    <lb-number
                        :key="gotoInputKey"
                        min="1"
                        label=""
                        hidedetails
                        :max="pagecount"
                        :show-min-max-error="false"
                        :allow-empty="true"
                        v-model="pageInput"
                        class="pagination-inline__goto-input"
                        data-testid="pagination__go-to-input"
                        @blur="goToPage"
                    />
                    <button
                        type="button"
                        class="pagination-inline__goto-btn"
                        data-testid="pagination__go-to-btn"
                        @click="goToPage"
                    >
                        Go
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
/**
 * Pagination Component
 * 
 * A reusable pagination component for table data.
 * Provides page navigation controls and "Go to page" input.
 * 
 * @example
 * <Pagination
 *   :list="filteredData"
 *   :itemsperpage="itemsPerPage"
 *   @paginated-data="setPaginatedData"
 * />
 */
export default {
    name: "Pagination",
    
    props: {
        /**
         * Array of items to paginate
         */
        list: {
            type: Array,
            default: () => [],
        },
        
        /**
         * Number of items per page
         */
        itemsperpage: {
            type: Number,
            default: 20,
        },

        /**
         * Server-side mode: total row count from API (current page rows live in `list` or parent state).
         */
        total: {
            type: Number,
            default: null,
        },

        /**
         * Current page (1-based) when using server-side `total`; kept in sync with parent after fetches.
         */
        page: {
            type: Number,
            default: null,
        },

        /**
         * Where to land when `page` is past the rows currently in `list`.
         * `last` keeps the last loaded page. `first` goes to page 1 (tasklist refresh
         * while later pages are still loading).
         */
        outOfRangePage: {
            type: String,
            default: 'last',
            validator: (value) => value === 'last' || value === 'first',
        },
    },
    
    data() {
        return {
            // Current page number (client-side or local UI state in server-side mode)
            currentPage: 1,
            // Draft value for jump-to-page input
            pageInput: 1,
            gotoInputKey: 0,
        };
    },
    
    computed: {
        serverSide() {
            return typeof this.total === 'number' && this.total >= 0;
        },

        /**
         * Calculate total number of pages
         */
        pagecount() {
            if (this.serverSide) {
                return Math.ceil(this.total / this.itemsperpage);
            }
            return Math.ceil(this.list.length / this.itemsperpage);
        },
        
        /**
         * Calculate total visible pages based on pagecount
         * Shows more pages when there are fewer total pages
         * Always keeps current page visible in the center
         */
        totalVisible() {
            // For smaller page counts, show all pages
            if (this.pagecount <= 7) {
                return this.pagecount;
            }
            // For larger counts, show 7 (current page centered with neighbors)
            return 7;
        },
    },
    
    watch: {
        /**
         * Emit paginated data when page changes
         */
        currentPage(newPage) {
            this.restorePageInput(newPage);
            this.emitPaginatedData();
        },
        
        /**
         * Adjust page when list changes (client-side mode)
         */
        list() {
            if (this.serverSide) {
                return;
            }
            const pageAdjusted = this.ensureValidPage();
            if (!pageAdjusted) {
                this.emitPaginatedData();
            }
        },

        total() {
            if (!this.serverSide) {
                return;
            }
            // Clamp page when total shrinks; do not emit here — parent already fetched.
            // If currentPage changes, the currentPage watcher emits once.
            this.ensureValidPage();
        },

        page: {
            immediate: true,
            handler(externalPage) {
                if (externalPage == null) {
                    return;
                }
                if (externalPage !== this.currentPage) {
                    this.currentPage = externalPage;
                    this.pageInput = externalPage;
                }
            },
        },
        
        /**
         * Adjust page when items per page changes
         */
        itemsperpage() {
            const pageAdjusted = this.ensureValidPage();
            if (!pageAdjusted) {
                this.emitPaginatedData();
            }
        }
    },
    
    methods: {
        /**
         * Ensure page number is within valid range
         * @returns {Boolean} True if page was adjusted, false otherwise
         */
        ensureValidPage() {
            const totalPages = this.pagecount;
            let targetPage;

            if (totalPages <= 0) {
                targetPage = 1;
            } else if (this.currentPage > totalPages) {
                targetPage = this.outOfRangePage === 'first' ? 1 : totalPages;
            } else if (this.currentPage < 1) {
                targetPage = 1;
            } else {
                targetPage = this.currentPage;
            }

            if (targetPage !== this.currentPage) {
                this.currentPage = targetPage;
                return true;
            }

            return false;
        },
        
        /**
         * Calculate and emit paginated data slice
         */
        emitPaginatedData() {
            if (this.ensureValidPage()) {
                return;
            }
            if (this.serverSide) {
                this.$emit('paginated-data', { data: [], page: this.currentPage });
                return;
            }
            const start = (this.currentPage - 1) * this.itemsperpage;
            const end = this.currentPage * this.itemsperpage;
            const paginatedData = this.list.slice(start, end);
            this.$emit('paginated-data', { data: paginatedData, page: this.currentPage });
        },
        
        /**
         * Jump to page from the right-side input (Figma "Go" control).
         * Page changes emit via the currentPage watcher; same-page Go restores the input.
         */
        goToPreviousPage() {
            if (this.currentPage > 1) {
                this.currentPage -= 1;
            }
        },

        goToNextPage() {
            if (this.currentPage < this.pagecount) {
                this.currentPage += 1;
            }
        },

        restorePageInput(page = this.currentPage) {
            const next = page;
            if (this.pageInput === next) {
                this.gotoInputKey += 1;
            }
            this.pageInput = next;
        },

        onGotoFocusOut(event) {
            const root = event.currentTarget;
            const next = event.relatedTarget;
            if (root && next && typeof root.contains === 'function' && root.contains(next)) {
                return;
            }
            this.goToPage();
        },

        goToPage() {
            const parsed = parseInt(String(this.pageInput ?? '').trim(), 10);
            if (!parsed || parsed < 1) {
                this.restorePageInput();
                return;
            }

            const lastPage = this.pagecount > 0 ? this.pagecount : 1;
            const nextPage = parsed > lastPage ? lastPage : parsed;
            if (nextPage === this.currentPage) {
                this.restorePageInput();
                return;
            }
            this.currentPage = nextPage;
        },

        /**
         * Programmatic page change (client-side ref sync, saved-page restore).
         * Replaces assigning to a removed writable `page` data field.
         */
        setPage(page) {
            const raw = page === '' || page == null ? NaN : Number(page);
            let nextPage;
            if (Number.isNaN(raw) || raw < 1) {
                nextPage = 1;
            } else if (raw > this.pagecount) {
                nextPage = this.pagecount > 0 ? this.pagecount : 1;
            } else {
                nextPage = Math.floor(raw);
            }

            this.pageInput = nextPage;
            if (nextPage === this.currentPage) {
                this.emitPaginatedData();
                return;
            }
            this.currentPage = nextPage;
        },
    },
    
    /**
     * Emit initial page data on mount
     */
    mounted() {
        this.pageInput = this.currentPage;
        if (!this.serverSide) {
            this.emitPaginatedData();
        }
        // Vuetify measures parent width on mount; reflow after layout so
        // maxButtons is correct and middle pages (3, 4, …) render.
        this.$nextTick(() => {
            window.dispatchEvent(new Event('resize'));
        });
    },
};
</script>

<style lang="scss" scoped>
@import './Pagination.scss';
</style>
